import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useBuildRun } from '../state/buildRun';
import { Button, Glass } from '../ui';
import ReportProblem from './ReportProblem';

/**
 * A build is a server job: POST /builds, poll every 3s, then finalizeBuild writes the
 * project and the router lands on the editor. This renders from store state and is
 * mounted globally, so navigating away does not cancel the build — the same design the
 * phone uses.
 */
const PHASES: Record<string, string> = {
  queued: 'Getting ready…',
  running: 'Building your app…',
  planning: 'Working out the plan…',
  generating: 'Writing your app…',
  enriching: 'Adding the finishing touches…',
  seeding: 'Filling it with example content…',
  // The store's own last two steps (state/buildRun.ts), which had no words and showed as the raw
  // "publishing…" in lower case (F15, 2026-09-30).
  publishing: 'Putting it online…',
  saving: 'Saving your app…',
  done: 'Almost there…',
};

/** An unrecognised phase still says something rather than leaving the panel blank. */
export function phaseLabel(phase: string): string {
  return PHASES[phase] ?? `${phase.replace(/[_-]+/g, ' ')}…`;
}

/** "1:23" — minutes and seconds since the build began. */
function clock(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The build's progress, and what to do when it fails.
 *
 * F15 (2026-09-30): for 70–180 seconds somebody saw a spinner and "Writing your app…" with no idea
 * how long it would be, under "You can keep browsing — this carries on." — on an overlay that
 * covered the whole page and could not be put away. Now it says how long a build usually takes and
 * counts the time so far, and "Keep browsing" is a button that puts it away to a pill, which is
 * the promise kept. While it covers the page it is a modal dialog that takes the focus.
 *
 * A failure always comes back out of the pill. And it offers only what can work: Try again for a
 * failure that might not repeat; Get credits for a refusal for credits; Sign in for a session
 * that has ended; nothing but Close for the rest the store marks terminal (a refused prompt, an
 * empty idea), which failed the same way on every press of the Try again they used to be offered.
 */
export default function BuildOverlay() {
  const status = useBuildRun((s) => s.status);
  const phase = useBuildRun((s) => s.phase);
  const error = useBuildRun((s) => s.error);
  const outOfCredits = useBuildRun((s) => s.outOfCredits);
  const terminal = useBuildRun((s) => s.terminal);
  const errorKind = useBuildRun((s) => s.errorKind);
  // In the store, which decides what a build that lands while put away does (state/buildRun.ts).
  const minimised = useBuildRun((s) => s.minimised);
  const landed = useBuildRun((s) => s.landed);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // The clock starts when a build does. A failure brings the panel back out of the pill (the store).
  useEffect(() => {
    if (status === 'running') {
      const at = Date.now();
      setStartedAt(at);
      setNow(at);
      const tick = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(tick);
    }
    setStartedAt(null);
    return undefined;
  }, [status]);

  // A landed build is offered until the person is on it, however they got there.
  const onLanded = !!landed && pathname === `/project/${landed.projectId}`;
  useEffect(() => {
    if (onLanded) useBuildRun.getState().forgetLanded();
  }, [onLanded]);

  // Behind the trial offer, the panel shows the progress itself (Paywall.tsx): nothing here.
  const behindOffer = useBuildRun((s) => s.behindOffer);
  // Also a refusal for credits behind the offer: the offer on the page is the answer (buildRun.ts).
  const offerIsTheAnswer = behindOffer && (status === 'running' || (status === 'error' && outOfCredits));
  const covering = status !== 'idle' && !(minimised && status === 'running') && !offerIsTheAnswer;
  // Into the dialog when it covers the page, so a keyboard or a screen reader starts there.
  useEffect(() => {
    if (covering) panel.current?.focus();
  }, [covering, status]);

  /*
   * The pill is 80px up, over the tab bar. The editor has no tab bar and a sticky chat composer
   * at the bottom instead, and there the pill covered the composer's input at 390px (review of
   * F15, 2026-09-30) — so it goes above the composer.
   */
  const pillClass = `build-pill glass pressable${pathname.startsWith('/project/') ? ' build-pill-over-composer' : ''}`;

  if (status === 'idle') {
    if (!landed || onLanded) return null;
    return (
      <button
        type="button"
        className={pillClass}
        onClick={() => useBuildRun.getState().openLanded()}
        aria-label={`${landed.name} is ready. Open it`}
      >
        <span className="type-caption">{`${landed.name} is ready`}</span>
        <span className="type-caption build-pill-open">Open</span>
      </button>
    );
  }

  const elapsed = clock(startedAt === null ? 0 : now - startedAt);

  if (offerIsTheAnswer) return null;
  if (minimised && status === 'running') {
    return (
      <button
        type="button"
        className={pillClass}
        onClick={() => useBuildRun.getState().expand()}
        aria-label={`${phaseLabel(phase)} ${elapsed} so far. Show build progress`}
      >
        <span className="build-spinner build-spinner-small" aria-hidden="true" />
        <span className="type-caption">{phaseLabel(phase)}</span>
        <span className="type-caption build-pill-time">{elapsed}</span>
      </button>
    );
  }

  function signIn(): void {
    useBuildRun.getState().dismiss();
    navigate('/sign-in', { state: { from: pathname + search } });
  }

  const signInNeeded = errorKind === 'unauthorized';

  return (
    <div className="build-overlay">
      <Glass className="build-panel">
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="build-dialog"
        >
          {status === 'error' ? (
            <>
              <h2 id={titleId} className="type-title">That didn&rsquo;t work</h2>
              <p role="alert" data-error-code={`build_${errorKind ?? 'failed'}`} className="type-body build-message">{error}</p>
              <div className="build-actions">
                {outOfCredits ? (
                  <Button title="Get credits" onPress={() => useBuildRun.getState().getCredits()} />
                ) : signInNeeded ? (
                  <Button title="Sign in" onPress={signIn} />
                ) : (
                  !terminal && <Button title="Try again" onPress={() => useBuildRun.getState().retry()} />
                )}
                <Button title="Close" variant="ghost" onPress={() => useBuildRun.getState().dismiss()} />
              </div>
              <p className="type-caption"><ReportProblem lastError={error} /></p>
            </>
          ) : (
            <>
              <h2 id={titleId} className="visually-hidden">Building your app</h2>
              <span className="build-spinner" aria-hidden="true" />
              <p role="status" aria-live="polite" className="type-title build-message">
                {phaseLabel(phase)}
              </p>
              <p className="type-body build-sub">
                This usually takes 2–3 minutes. <span className="build-time">{elapsed}</span> so far.
              </p>
              <Button title="Keep browsing" variant="ghost" onPress={() => useBuildRun.getState().minimise()} />
            </>
          )}
        </div>
      </Glass>
    </div>
  );
}
